import { HttpErrorResponse } from "@angular/common/http";
import { Component, computed, effect, EventEmitter, input, Output, signal } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Guid } from "moo-shared";
import { catchError, finalize, map, of, switchMap, tap, throwError } from "rxjs";
import { AuthenticationApiService } from "../../../core/authentication/authentication-api.service";
import { AuthenticationErrors } from "../../../core/authentication/authentication-errors";
import { AuthenticationServerService } from "../../../core/authentication/authentication-server.service";
import { EnvironmentService } from "../../../core/environment.service";
import { IHttpErrorResponseBody } from "../../../core/http-error-response-body.model";
import { BaseComponent } from "../../../shared/base.component";
import { isEmptyString } from "../../../shared/helpers";
import { LoginAssistantService } from "../../login-assistant/login-assistant.service";
import { QrCredentials } from "../qr-login/qr-login.component";
import { ChromeDeviceService } from "./chrome-device-service";
import { LoginPictureModel } from "./login-picture.viewmodel";

@Component({
  selector: "app-picture-login",
  templateUrl: "./picture-login.component.html"
})
export class PictureLoginComponent extends BaseComponent {

  override componentTranslationRoot = "login.picture-login";

  @Output() tokenAcquired = new EventEmitter<string>();
  @Output() formCloseRequested = new EventEmitter();
  inputPictures = input<LoginPictureModel[]>([]);
  private hasInputPictures = computed<boolean>(() => (this.inputPictures()?.length ?? 0) > 0);

  qrCredentials = input<QrCredentials>();

  selectedPictureKeys = signal<Guid[]>([]);
  picturesForLoginName = signal<LoginPictureModel[]>([]);

  availablePictures = computed<LoginPictureModel[]>(() => this.hasInputPictures() ?
    this.inputPictures().map(x => new LoginPictureModel(x.key, x.name, this.environmentService.staticFilesUrl))
    : this.picturesForLoginName());

  notificationMessage = signal<[string, boolean]>(["", false]);

  accountName = input.required<string>();

  constructor(
    private readonly authenticationServerService: AuthenticationServerService,
    translateService: TranslateService,
    private readonly loginAssistantService: LoginAssistantService,
    private readonly chromeDeviceService: ChromeDeviceService,
    private readonly authenticationApiService: AuthenticationApiService,
    private readonly environmentService: EnvironmentService
  ) {
    super(translateService);
    effect(() => {
      if (!isEmptyString(this.accountName())) {
        this.chromeDeviceService.suppressGoogleLoginForm(this.accountName());
      }
      else {
        this.picturesForLoginName.set([]);
      }

      if (!isEmptyString(this.accountName()) && !this.hasInputPictures()) {
        this.subscriptionsToRelease.push(
          this.authenticationApiService.getPictureLoginOptions(this.accountName())
            .subscribe(pictures => {
              this.picturesForLoginName.set(pictures.map(x => LoginPictureModel.fromDto(x, this.environmentService.staticFilesUrl)));
              this.selectedPictureKeys.set([]);
            }));
      }
    }, { allowSignalWrites: true });

    effect(() => {
      if (this.selectedPictureKeys().length === 3) {
        this.tryGetToken();
      }
    });
  }

  tryGetToken() {
    const qrCredentials = this.qrCredentials();
    const getToken = qrCredentials ? this.authenticationServerService.authenticateUsingQrCodeAndPictures(this.accountName(), qrCredentials.qrCodeKey, qrCredentials.qrVersionKey, this.selectedPictureKeys())
      : this.authenticationServerService.authenticateUsingPictures(this.accountName(), this.selectedPictureKeys());

    this.subscriptionsToRelease.push(getToken.pipe(
      finalize(() => this.selectedPictureKeys.set([])),
      catchError(error => throwError(() => this.getErrorKeyFromResponse(error))),
      switchMap(token => {
        if (this.chromeDeviceService.isSignInWebview) {
          // Deliberately not waitForChromeHandoff(): isSignInWebview means registration already
          // succeeded, so a missing complete() ack is a real failure to surface, not swallow.
          this.chromeDeviceService.completeRegistration();
          return this.chromeDeviceService.registrationCompleted().pipe(
            map(() => token),
            catchError(() => throwError(() => "can-not-connect-to-chrome"))
          );
        }

        return of(token);
      }),
      tap(token => this.tokenAcquired.emit(token)),
      catchError((error) => of(this.handleError(error)))
    ).subscribe());
  }

  private getErrorKeyFromResponse(response: HttpErrorResponse): string {
    const responseBody = response.error as IHttpErrorResponseBody;
    const errorKey = AuthenticationErrors.getErrorTranslationKeyForPictureOrQrLogin(responseBody?.Code);
    return errorKey;
  }

  private handleError(errorKey: string): void {
    this.notificationMessage.update(value => [this.translate(`login-errors.${errorKey}`), !value[1]]);
  }

  goToPreviousScreen(): void {
    this.loginAssistantService.redirectToLastAssistantScreen();
  }
}
